import 'react-native-url-polyfill/auto'
import './global.css'
import { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import {
  useFonts,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_900Black,
} from '@expo-google-fonts/inter'
import * as Sentry from '@sentry/react-native'
import * as SplashScreen from 'expo-splash-screen'
import { StatusBar } from 'expo-status-bar'
import { initI18n } from './src/i18n'
import { AdminNavigation, CashierNavigationWithSessionGate, cashierNavigationRef } from './src/navigation'
import { ErrorBoundary } from './src/ErrorBoundary'
import { supabase } from './src/lib/supabase'
import { reportOnFinalRetry } from './src/lib/reportError'
import { isDeviceForbiddenError } from './src/lib/deviceAuthError'

// Same codebase serves the native cashier app and the web admin dashboard —
// each reports to its own Sentry project, selected by platform. Skipped in
// dev mode (Metro/hot reload, or an EAS build with developmentClient: true)
// so local errors don't get reported — only release-mode builds (EAS
// preview/production) initialize Sentry.
if (!__DEV__) {
  Sentry.init({
    dsn: Platform.OS === 'web'
      ? process.env.EXPO_PUBLIC_SENTRY_DSN_ADMIN
      : process.env.EXPO_PUBLIC_SENTRY_DSN_CASHIER,
  })
}

SplashScreen.preventAutoHideAsync()

// A deactivated device's access token stays valid until it naturally
// expires — deactivation doesn't revoke it — so the cashier app can keep
// making requests against a device the admin already turned off. Every
// cashier Edge Function independently rejects those with 401 (auth.getUser
// fails outright, since deactivation deletes the underlying auth user) or
// 403 (the narrower case where the user still resolves but fails the
// account_type/device-active check) — see isDeviceForbiddenError. Catching
// that here, once, globally across every query and mutation, forces an
// immediate sign-out and drop back to the pairing screen instead of waiting
// for app relaunch.
function handlePossibleDeviceForbidden(error: unknown) {
  if (Platform.OS === 'web' || !isDeviceForbiddenError(error)) return
  if (cashierNavigationRef.isReady() && cashierNavigationRef.getCurrentRoute()?.name === 'Pairing') return

  void supabase.auth.signOut().finally(() => {
    if (cashierNavigationRef.isReady()) {
      cashierNavigationRef.resetRoot({ index: 0, routes: [{ name: 'Pairing' }] })
    }
  })
}

// Default retry reports once, on the final failed attempt, instead of
// silently giving up. The two 5-min polling queries (cashier feature flags,
// stamp settings) are low-frequency enough that this default is fine as-is.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: reportOnFinalRetry },
  },
  queryCache: new QueryCache({ onError: handlePossibleDeviceForbidden }),
  mutationCache: new MutationCache({ onError: handlePossibleDeviceForbidden }),
})

function App() {
  const [i18nReady, setI18nReady] = useState(false)
  const [cashierSessionReady, setCashierSessionReady] = useState(Platform.OS === 'web')

  const [fontsLoaded, fontError] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Inter_900Black,
  })

  useEffect(() => {
    initI18n()
      .then(() => setI18nReady(true))
      .catch(() => setI18nReady(true))
  }, [])

  useEffect(() => {
    if ((fontsLoaded || fontError) && i18nReady && cashierSessionReady) {
      void SplashScreen.hideAsync()
    }
  }, [fontsLoaded, fontError, i18nReady, cashierSessionReady])

  if (!fontsLoaded && !fontError) return null
  if (!i18nReady) return null

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <QueryClientProvider client={queryClient}>
          <StatusBar style="auto" />
          <ErrorBoundary>
            {Platform.OS === 'web' ? (
              <AdminNavigation />
            ) : (
              <CashierNavigationWithSessionGate
                onSessionReady={() => setCashierSessionReady(true)}
              />
            )}
          </ErrorBoundary>
        </QueryClientProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  )
}

export default Sentry.wrap(App)
